import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import type { ThunkConf } from 'store/thunks';
import { BatchReturn, BatchDestination, BatchReturnStatusType, ReturnStatusType } from 'returnCenter-api';
import { batchReturnService } from 'services/batchReturnService';
import { batchReturnProcessingService } from 'services/batchReturnProcessingService';
import { companyChanged } from 'store/actions/companyActions';
import {
    BatchReturnStatusStatistic,
    BatchReturnSearchRequest,
    BatchReturnOverview,
    BatchReturnPageInfo,
} from 'types/batchReturn';
import {
    BatchReturnProcessingSummary,
    BatchReturnProcessingResult,
    BatchPackageReturnSlipResult,
    BatchCloseRemainingResult,
    BatchCreditResult,
} from 'types/batchReturnProcessing';
import { ApiActionError, toApiActionError } from 'utils/apiError';

/** The backend's { error, code } for a refused batch package action, when there is one. */
export type BatchPackageActionError = ApiActionError;
const toPackageActionError = toApiActionError;

interface BatchReturnState {
    destinations: BatchDestination[];
    openBatchReturns: BatchReturn[];
    loading: boolean;
    error: string | null;
    // Statistics
    statistics: BatchReturnStatusStatistic[];
    statisticsLoading: boolean;
    // Search
    searchResults: BatchReturnOverview[];
    searchPageInfo: { currentPage: number; totalPages: number; totalItems: number; pageSize: number } | null;
    searchLoading: boolean;
    // Card loading indicators
    loadingCards: Partial<Record<string, boolean>>;
    // Current batch return detail
    currentBatchReturn: BatchReturn | null;
    currentLoading: boolean;
    currentError: string | null;
    // Processing
    processingSummary: BatchReturnProcessingSummary | null;
    processingSummaryLoading: boolean;
    processingError: string | null;
    processingCompanyCode: string | null;
    /** Handled return slips ticked for crediting together. */
    creditSelection: number[];
}

const initialState: BatchReturnState = {
    destinations: [],
    openBatchReturns: [],
    loading: false,
    error: null,
    statistics: [],
    statisticsLoading: false,
    searchResults: [],
    searchPageInfo: null,
    searchLoading: false,
    loadingCards: {},
    currentBatchReturn: null,
    currentLoading: false,
    currentError: null,
    processingSummary: null,
    processingSummaryLoading: false,
    processingError: null,
    processingCompanyCode: null,
    creditSelection: [],
};

// ============ THUNKS ============

export const fetchBatchDestinations = createAsyncThunk<BatchDestination[], void, ThunkConf>(
    'batchReturn/fetchDestinations',
    async (_, { rejectWithValue }) => {
        try {
            return await batchReturnService.getBatchDestinations();
        } catch (error) {
            return rejectWithValue({ message: 'Failed to fetch batch destinations' });
        }
    },
);

export const fetchOpenBatchReturns = createAsyncThunk<BatchReturn[], void, ThunkConf>(
    'batchReturn/fetchOpenBatchReturns',
    async (_, { rejectWithValue }) => {
        try {
            return await batchReturnService.getBatchReturns(BatchReturnStatusType.Open);
        } catch (error) {
            return rejectWithValue({ message: 'Failed to fetch open batch returns' });
        }
    },
);

export const createBatchReturn = createAsyncThunk<
    BatchReturn,
    { batchDestinationId: number; comment?: string },
    ThunkConf
>('batchReturn/create', async ({ batchDestinationId, comment }, { rejectWithValue, dispatch }) => {
    try {
        const result = await batchReturnService.createBatchReturn({ batchDestinationId, comment });
        dispatch(fetchBatchReturnStatistics());
        return result;
    } catch (error) {
        return rejectWithValue({ message: 'Failed to create batch return' });
    }
});

export const fetchBatchReturnStatistics = createAsyncThunk<BatchReturnStatusStatistic[], void, ThunkConf>(
    'batchReturn/fetchStatistics',
    async (_, { rejectWithValue }) => {
        try {
            return await batchReturnService.getBatchReturnStatistics();
        } catch (error) {
            return rejectWithValue({ message: 'Failed to fetch batch return statistics' });
        }
    },
);

export const searchBatchReturns = createAsyncThunk<BatchReturnPageInfo, BatchReturnSearchRequest, ThunkConf>(
    'batchReturn/search',
    async (request, { rejectWithValue }) => {
        try {
            return await batchReturnService.searchBatchReturns(request);
        } catch (error) {
            return rejectWithValue({ message: 'Failed to search batch returns' });
        }
    },
);

export const fetchBatchReturnById = createAsyncThunk<BatchReturn, number, ThunkConf>(
    'batchReturn/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            return await batchReturnService.getBatchReturnById(id);
        } catch (error) {
            return rejectWithValue({ message: 'Failed to fetch batch return' });
        }
    },
);

export const updateBatchReturnStatus = createAsyncThunk<void, { id: number; status: BatchReturnStatusType }, ThunkConf>(
    'batchReturn/updateStatus',
    async ({ id, status }, { rejectWithValue, dispatch }) => {
        try {
            await batchReturnService.updateBatchReturnStatus(id, status);
            dispatch(fetchBatchReturnStatistics());
        } catch (error) {
            return rejectWithValue({ message: 'Failed to update batch return status' });
        }
    },
);

export const receiveBatchReturn = createAsyncThunk<void, number, ThunkConf>(
    'batchReturn/receive',
    async (id, { rejectWithValue, dispatch }) => {
        try {
            await batchReturnService.receiveBatchReturn(id);
            dispatch(fetchBatchReturnStatistics());
        } catch (error) {
            return rejectWithValue({ message: 'Failed to receive batch return' });
        }
    },
);

export const fetchProcessingSummary = createAsyncThunk<BatchReturnProcessingSummary, number, ThunkConf>(
    'batchReturn/fetchProcessingSummary',
    async (batchReturnId, { rejectWithValue }) => {
        try {
            return await batchReturnProcessingService.getProcessingSummary(batchReturnId);
        } catch (error) {
            return rejectWithValue({ message: 'Failed to fetch processing summary' });
        }
    },
);

export const processReturns = createAsyncThunk<
    BatchReturnProcessingResult,
    { batchReturnId: number; returnIds: number[] },
    ThunkConf
>('batchReturn/processReturns', async ({ batchReturnId, returnIds }, { rejectWithValue, dispatch }) => {
    try {
        const result = await batchReturnProcessingService.processReturns(batchReturnId, returnIds);
        dispatch(fetchBatchReturnStatistics());
        return result;
    } catch (error) {
        return rejectWithValue({ message: 'Failed to process returns' });
    }
});

export const createReturnSlipForPackage = createAsyncThunk<
    BatchPackageReturnSlipResult,
    { batchReturnId: number; sourceReturnId: number },
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/createReturnSlipForPackage', async ({ batchReturnId, sourceReturnId }, { rejectWithValue }) => {
    try {
        return await batchReturnProcessingService.createReturnSlipForPackage(batchReturnId, sourceReturnId);
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to create return slip for package'));
    }
});

export const markPackageMissing = createAsyncThunk<
    void,
    { batchReturnId: number; sourceReturnId: number },
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/markPackageMissing', async ({ batchReturnId, sourceReturnId }, { rejectWithValue, dispatch }) => {
    try {
        await batchReturnProcessingService.markPackageMissing(batchReturnId, sourceReturnId);
        dispatch(fetchBatchReturnStatistics());
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to mark package as missing'));
    }
});

export const unmarkPackageMissing = createAsyncThunk<
    void,
    { batchReturnId: number; sourceReturnId: number },
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/unmarkPackageMissing', async ({ batchReturnId, sourceReturnId }, { rejectWithValue }) => {
    try {
        await batchReturnProcessingService.unmarkPackageMissing(batchReturnId, sourceReturnId);
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to undo missing package'));
    }
});

export const closeRemainingAsMissing = createAsyncThunk<
    BatchCloseRemainingResult,
    number,
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/closeRemainingAsMissing', async (batchReturnId, { rejectWithValue, dispatch }) => {
    try {
        const result = await batchReturnProcessingService.closeRemainingAsMissing(batchReturnId);
        dispatch(fetchBatchReturnStatistics());
        return result;
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to close remaining packages'));
    }
});

export const completeBatch = createAsyncThunk<void, number, ThunkConf & { rejectValue: BatchPackageActionError }>(
    'batchReturn/completeBatch',
    async (batchReturnId, { rejectWithValue, dispatch }) => {
        try {
            await batchReturnProcessingService.completeBatch(batchReturnId);
            dispatch(fetchBatchReturnStatistics());
        } catch (error) {
            return rejectWithValue(toPackageActionError(error, 'Failed to complete the batch'));
        }
    },
);

export const reopenBatch = createAsyncThunk<
    void,
    /** With the package that turned up, its missing mark goes in the same step. */
    { batchReturnId: number; comment: string; sourceReturnId?: number },
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/reopenBatch', async ({ batchReturnId, comment, sourceReturnId }, { rejectWithValue, dispatch }) => {
    try {
        await batchReturnProcessingService.reopenBatch(batchReturnId, comment, sourceReturnId);
        dispatch(fetchBatchReturnStatistics());
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to reopen the batch'));
    }
});

export const creditReturnSlips = createAsyncThunk<
    BatchCreditResult,
    { batchReturnId: number; returnSlipIds: number[] },
    ThunkConf & { rejectValue: BatchPackageActionError }
>('batchReturn/creditReturnSlips', async ({ batchReturnId, returnSlipIds }, { rejectWithValue, dispatch }) => {
    try {
        const result = await batchReturnProcessingService.creditReturnSlips(batchReturnId, returnSlipIds);
        dispatch(fetchBatchReturnStatistics());
        return result;
    } catch (error) {
        return rejectWithValue(toPackageActionError(error, 'Failed to credit the packages'));
    }
});

// ============ SLICE ============

const batchReturnSlice = createSlice({
    name: 'batchReturn',
    initialState,
    reducers: {
        resetBatchReturnState: () => initialState,
        resetCurrentBatchReturn: (state) => {
            state.currentBatchReturn = null;
            state.currentLoading = false;
            state.currentError = null;
            state.creditSelection = [];
        },
        toggleCreditSelection: (state, action: PayloadAction<number>) => {
            state.creditSelection = state.creditSelection.includes(action.payload)
                ? state.creditSelection.filter((id) => id !== action.payload)
                : [...state.creditSelection, action.payload];
        },
        clearCreditSelection: (state) => {
            state.creditSelection = [];
        },
        setBatchCardLoading: (state, action: PayloadAction<{ status: string; isLoading: boolean }>) => {
            state.loadingCards[action.payload.status] = action.payload.isLoading;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBatchDestinations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBatchDestinations.fulfilled, (state, action) => {
                state.loading = false;
                state.destinations = action.payload;
            })
            .addCase(fetchBatchDestinations.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as any)?.message ?? 'Unknown error';
            })
            .addCase(fetchOpenBatchReturns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOpenBatchReturns.fulfilled, (state, action) => {
                state.loading = false;
                state.openBatchReturns = action.payload;
            })
            .addCase(fetchOpenBatchReturns.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as any)?.message ?? 'Unknown error';
            })
            .addCase(createBatchReturn.fulfilled, (state, action) => {
                state.openBatchReturns.push(action.payload);
            })
            // Statistics
            .addCase(fetchBatchReturnStatistics.pending, (state) => {
                state.statisticsLoading = true;
            })
            .addCase(fetchBatchReturnStatistics.fulfilled, (state, action) => {
                state.statisticsLoading = false;
                state.statistics = action.payload;
            })
            .addCase(fetchBatchReturnStatistics.rejected, (state) => {
                state.statisticsLoading = false;
            })
            // Search
            .addCase(searchBatchReturns.pending, (state) => {
                state.searchLoading = true;
            })
            .addCase(searchBatchReturns.fulfilled, (state, action) => {
                state.searchLoading = false;
                state.searchResults = action.payload.items;
                state.searchPageInfo = {
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    totalItems: action.payload.totalItems,
                    pageSize: action.payload.pageSize,
                };
                // Clear card loading for this status
                Object.keys(state.loadingCards).forEach((key) => {
                    state.loadingCards[key] = false;
                });
            })
            .addCase(searchBatchReturns.rejected, (state) => {
                state.searchLoading = false;
            })
            // Current batch return detail
            .addCase(fetchBatchReturnById.pending, (state) => {
                state.currentLoading = true;
                state.currentError = null;
            })
            .addCase(fetchBatchReturnById.fulfilled, (state, action) => {
                state.currentLoading = false;
                state.currentBatchReturn = action.payload;
            })
            .addCase(fetchBatchReturnById.rejected, (state, action) => {
                state.currentLoading = false;
                state.currentError = (action.payload as any)?.message ?? 'Unknown error';
            })
            .addCase(updateBatchReturnStatus.fulfilled, (state, action) => {
                if (state.currentBatchReturn) {
                    state.currentBatchReturn.status = action.meta.arg.status;
                }
            })
            // Processing
            .addCase(fetchProcessingSummary.pending, (state) => {
                state.processingSummaryLoading = true;
                state.processingError = null;
            })
            .addCase(fetchProcessingSummary.fulfilled, (state, action) => {
                state.processingSummaryLoading = false;
                state.processingSummary = action.payload;
            })
            .addCase(fetchProcessingSummary.rejected, (state, action) => {
                state.processingSummaryLoading = false;
                state.processingError = (action.payload as any)?.message ?? 'Unknown error';
            })
            .addCase(processReturns.pending, (state) => {
                state.processingCompanyCode = 'processing';
            })
            .addCase(processReturns.fulfilled, (state) => {
                state.processingCompanyCode = null;
            })
            .addCase(processReturns.rejected, (state) => {
                state.processingCompanyCode = null;
            })
            .addCase(companyChanged, () => initialState);
    },
});

// ============ SELECTORS ============

export const selectBatchDestinations = (state: RootState) => state.batchReturn.destinations;
export const selectOpenBatchReturns = (state: RootState) => state.batchReturn.openBatchReturns;
export const selectBatchReturnLoading = (state: RootState) => state.batchReturn.loading;
export const selectBatchReturnStatistics = (state: RootState) => state.batchReturn.statistics;
export const selectBatchStatisticsLoading = (state: RootState) => state.batchReturn.statisticsLoading;
export const selectBatchSearchResults = (state: RootState) => state.batchReturn.searchResults;
export const selectBatchSearchPageInfo = (state: RootState) => state.batchReturn.searchPageInfo;
export const selectBatchSearchLoading = (state: RootState) => state.batchReturn.searchLoading;
export const selectBatchLoadingCards = (state: RootState) => state.batchReturn.loadingCards;
export const selectCurrentBatchReturn = (state: RootState) => state.batchReturn.currentBatchReturn;
export const selectCurrentBatchReturnLoading = (state: RootState) => state.batchReturn.currentLoading;
export const selectCurrentBatchReturnError = (state: RootState) => state.batchReturn.currentError;
export const selectProcessingSummary = (state: RootState) => state.batchReturn.processingSummary;
export const selectProcessingSummaryLoading = (state: RootState) => state.batchReturn.processingSummaryLoading;
export const selectProcessingError = (state: RootState) => state.batchReturn.processingError;
export const selectProcessingCompanyCode = (state: RootState) => state.batchReturn.processingCompanyCode;
export const selectCreditSelection = (state: RootState) => state.batchReturn.creditSelection;

// ============ CALCULATED SELECTORS ============
export const selectCurrentBatchReturnItems = createSelector(selectCurrentBatchReturn, (batchReturn) => {
    if (!batchReturn) return [];
    return batchReturn.returns?.flatMap((r) => r.items ?? []) ?? [];
});

export const selectIsReturnStatusCompleted = createSelector(selectCurrentBatchReturn, (batchReturn) => {
    if (!batchReturn || !batchReturn.returns || batchReturn.returns.length === 0) return false;
    return batchReturn.returns.every((r) => r.returnStatus === ReturnStatusType.Completed);
});

export const {
    resetBatchReturnState,
    resetCurrentBatchReturn,
    setBatchCardLoading,
    toggleCreditSelection,
    clearCreditSelection,
} = batchReturnSlice.actions;
export default batchReturnSlice.reducer;
